import { jwtDecode, JwtPayload } from "jwt-decode";

interface CustomJwtPayload extends JwtPayload {
    permissions?: string[];
}

const token = localStorage.getItem('token');
const decodedToken = token ? jwtDecode<CustomJwtPayload>(token) : null;
const permissions = decodedToken?.permissions || [];

// Debug log permissions
console.log('Available permissions:', permissions);

const filterMenuItemByPermission = (item: MenuItem): boolean => {
    if (!item.permission) return true;
    const hasPermission = permissions.includes(item.permission);
    console.log(`Checking permission for ${item.title}: ${item.permission} -> ${hasPermission}`);
    return hasPermission;
};

const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(item => {
        // console.log(`Processing menu item: ${item.title}`);
        
        // For items with children
        if (item.children) {
            // console.log(`${item.title} has ${item.children.length} children`);
            
            // Recursively filter children first
            const filteredChildren = filterMenuItems(item.children);
            // console.log(`${item.title} filtered children: ${filteredChildren.length}`);
            
            // Always update the children array with filtered results
            item.children = filteredChildren;
            
            // For sub-menu items
            if (item.type === 'sub') {
                const shouldShow = !item.permission || filterMenuItemByPermission(item) || filteredChildren.length > 0;
                // console.log(`Sub-menu ${item.title} should show: ${shouldShow}`);
                return shouldShow;
            }
        }
        
        // For items without children or non-sub items
        const hasPermission = filterMenuItemByPermission(item);
        // console.log(`Menu item ${item.title} has permission: ${hasPermission}`);
        return hasPermission;
    });
};

// Filter the menu sections
const filterMenuSections = (sections: MenuSection[]): MenuSection[] => {
    console.log('Filtering menu sections...');
    return sections.filter(section => {
        console.log(`Processing section: ${section.title}`);
        
        // Filter children of this section
        const filteredChildren = filterMenuItems(section.children);
        console.log(`Section ${section.title} has ${filteredChildren.length} filtered children`);
        
        // Update section's children
        section.children = filteredChildren;
        
        // Keep section if it has any visible children
        return filteredChildren.length > 0;
    });
};

interface MenuItem {
    path?: string;
    title: string;
    icon?: string;
    type: "link" | "sub";
    active?: boolean;
    children?: MenuItem[];
    permission?: string;
    badge?: boolean;
}

interface MenuSection {
    title: string;
    active: boolean;
    children: MenuItem[];
}

interface MenuData {
    data: MenuSection[];
}

// Create the menu data and filter it
const rawMenu: MenuData = {
    data: [
        {
            title: "Dashboard",
            active: false,
            children: [
                {
                    path: "/",
                    title: "Dashboard",
                    icon: "home",
                    active: false,
                    type: "link",
                    permission: "view_dashboard"
                }
            ]
        },
        {
            title: "Marketing",
            active: false,
            children: [
                {
                    path: "/field-employee-day/list",
                    title: "Field Employee Day",
                    type: "link",
                    icon: "users",
                    permission: "view_field_employee_day",
                    badge: true
                },
                {
                    path: "/assign-marketing-work/list",
                    title: "Assign Work",
                    type: "link",
                    icon: "briefcase",
                    permission: "view_assign_work"
                },
                {
                    path: "/assign-work-approval/list",
                    title: "Assign Work Approval",
                    type: "link",
                    icon: "check-square",
                    permission: "view_assign_work_approval"
                },
                {
                    title: "Marketing Reports",
                    icon: "briefcase",
                    type: "sub",
                    active: false,
                    permission: "view_reports",
                    children: [
                        {
                            path: "/reports/percentage",
                            title: "Percentage analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/area",
                            title: "Area analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/marketing-kyt-change-transfer",
                            title: "Marketing KYT Change Transfer",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/criteria",
                            title: "Criteria",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/nfd-analysis",
                            title: "NFD Analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/project",
                            title: "Project report",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/soc",
                            title: "SOC analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/competitor",
                            title: "Competitor analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/project-with",
                            title: "Project with",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/projection",
                            title: "Projection and status",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/just-analysis",
                            title: "Just analysis",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/no-of-project-converted",
                            title: "Project Converted",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/assign-work",
                            title: "Assign Work",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/cdw-given",
                            title: "Cdw Given",
                            type: "link",
                            permission: "view_reports"
                        },
                        {
                            path: "/reports/quotations-proposal",
                            title: "Quotations Proposal",
                            type: "link",
                            permission: "view_reports"
                        }
                    ]
                },
                {
                    path: "/quotation/list",
                    title: "Quotation",
                    type: "link",
                    icon: "file-text",
                    active: false,
                    permission: "view_quotations"
                },
                {
                    path: "/app-quoats/list",
                    title: "App Quotes",
                    type: "link",
                    icon: "smartphone",
                    active: false,
                    permission: "view_app_quotes"
                },
                {
                    path:"/ratechart/list",
                    title:"Rate Chart",
                    icon: "bar-chart",
                    type:"link",
                    permission: "view_rate_chart"
                },
                // {
                //     path: "/kyts/marketing",
                //     title: "Marketing KYT",
                //     type: "link",
                //     icon: "clipboard",
                //     permission: "view_marketing_kyt"
                // },
                {
                    path: "/marketing-kyt-approval/list",
                    title: "Marketing KYT Approval",
                    icon: "check-circle",
                    type: "link",
                    badge: true,
                    permission: "view_marketing_kyt_approval"
                }
            ]
        },
        {
            title: "Website Management",
            active: false,
            children: [
                {
                    title: "Website",
                    icon: "film",
                    type: "sub",
                    active: false,
                    permission: "view_website",
                    children: [
                        {
                            path: "/pages/home",
                            title: "Home",
                            icon: "film",
                            type: "link",
                            permission: "view_home"
                        },
                        {
                            path: "/pages/services-page",
                            title: "Services",
                            icon: "airplay",
                            type: "link",
                            permission: "view_services"
                        },
                        {
                            path: "/pages/whyus",
                            title: "Why Us",
                            icon: "airplay",
                            type: "link",
                            permission: "view_whyus"
                        },
                        {
                            path: "/pages/gallery",
                            title: "Gallery",
                            icon: "box",
                            type: "link",
                            permission: "view_gallery"
                        },
                        {
                            path: "/pages/contact-us",
                            title: "Contact Us",
                            icon: "box",
                            type: "link",
                            permission: "view_contact"
                        },
                        {
                            path: "/pages/testimonials",
                            title: "Testimonials",
                            icon: "box",
                            type: "link",
                            permission: "view_testimonials"
                        },
                        {
                            path: "/pages/apps",
                            title: "Apps Page",
                            icon: "monitor",
                            type: "link",
                            permission: "view_apps"
                        },
                        {
                            path: "/pages/join-us",
                            title: "Join Us",
                            icon: "shopping-bag",
                            type: "link",
                            permission: "view_joinus"
                        },
                        {
                            path: "/pages/clients",
                            title: "Clients",
                            icon: "shopping-bag",
                            type: "link",
                            permission: "view_clients"
                        }
                    ]
                },
                {
                    path: "/app/contacts_data",
                    title: "Contacts",
                    icon: "mail",
                    type: "link",
                    active: false,
                    permission: "view_contacts"
                },
                {
                    path: "/app/file_manager",
                    title: "File manager",
                    icon: "git-pull-request",
                    type: "link",
                    active: false,
                    permission: "view_files"
                }
            ]
        },        {
            title:"Tools",
            active: false,
            
            children: [
                {
                    title: "Tools",
                    icon: "tool",
                    type: "sub",
                    active: false,
                    permission: "view_tools",
                    children: [
                        {
                            path: "/image/processing",
                            title: "Image Processing",
                            type: "link",
                            icon: "image",
                            permission: "view_image_processing"
                        }
                    ]
                }
            ]
        },
        {
            title: "Marketing & Operations",
            active: false,
            children: [
                {
                    title: "Operations",
                    icon: "settings",
                    type: "sub",
                    active: false,
                    permission: "view_operation",
                    children: [
                        {
                            path: "/operation/transfer",
                            title: "Transfer",
                            type: "link",
                            permission: "view_transfer"
                        },
                        {
                            path: "/operation/exchange",
                            title: "Exchange",
                            type: "link",
                            permission: "view_exchange"
                        },
                        {
                            path: "/operation/kyp",
                            title: "KYP",
                            type: "link",
                            permission: "view_kyp"
                        },
                        {
                            path: "/operation/advance-salary-request",
                            title: "Advance Salary Request",
                            type: "link",
                            permission: "view_advance_salary"
                        },
                        {
                            path: "/operation/reinstate",
                            title: "Reinstate",
                            type: "link",
                            permission: "view_reinstate"
                        },
                        {
                            path: "/operation/siddar",
                            title: "SIDDAR",
                            type: "link",
                            permission: "view_siddar"
                        },
                        {
                            path: "/operation/change-siddars",
                            title: "Change Siddars",
                            type: "link",
                            permission: "view_change_siddars"
                        },
                        {
                            path: "/operation/frequency",
                            title: "Frequency",
                            type: "link",
                            permission: "view_frequency"
                        },
                        {
                            path: "/operation/manpower-contribution",
                            title: "Manpower Contribution",
                            type: "link",
                            permission: "view_manpower_contribution"
                        },
                        {
                            path: "/operation/reliver-taken",
                            title: "Reliver Taken",
                            type: "link",
                            permission: "view_reliver_taken"
                        },
                        {
                            path: "/operation/marketing-lead",
                            title: "Marketing Lead",
                            type: "link",
                            permission: "view_marketing_lead"
                        },
                        {
                            path: "/operation/material-request",
                            title: "Material Request",
                            type: "link",
                            permission: "view_material_request"
                        },
                        {
                            path: "/cdw/list",
                            title: "CDW",
                            type: "link",
                            permission: "view_cdw"
                        }
                    ]
                }
            ]
        },
        {
            title: "Employee Management",
            active: false,
            children: [
                {
                    title: "Employees",
                    icon: "users",
                    type: "sub",
                    active: false,
                    permission: "view_employees",
                    children: [
                        {
                            path: "/employee-list/all-employee-list",
                            title: "All Employee List",
                            type: "link",
                            permission: "view_all_employees"
                        },
                        {
                            path: "/employees/field",
                            title: "Field Employees",
                            icon: "users",
                            type: "link",
                            permission: "view_field_employees"
                        },
                        {
                            path: "/employees/operation",
                            title: "Operation Employees",
                            icon: "users",
                            type: "link",
                            permission: "view_operation_employees"
                        },
                        {
                            path: "/employee-list/black-listed-employee",
                            title: "Black Listed Employee",
                            type: "link",
                            permission: "view_blacklisted_employees"
                        }
                    ]
                },
                {
                    path: "/import-employee/add",
                    title: "Import Employee",
                    icon: "clipboard",
                    type: "link",
                    active: false,
                    permission: "view_import_employee"
                },
                {
                    path: "/locked-employees/list",
                    title: "Locked Employees",
                    icon: "clipboard",
                    type: "link",
                    active: false,
                    permission: "view_locked_employees"
                },
                {
                    path: "/leaves/list",
                    title: "Leaves",
                    icon: "calendar",
                    type: "link",
                    permission: "view_leaves",
                    badge: true
                },
                {
                    path: "/attendance/list",
                    title: "Attendance",
                    type: "link",
                    permission: "view_attendance"
                },
                {
                    path: "/attendance/late-attendance",
                    title: "Late Attendance",
                    type: "link",
                    permission: "view_late_attendance"
                },
                {
                    path: "/attendance/employee-late-claim",
                    title: "Employee Late Claim",
                    type: "link",
                    permission: "view_employee_late_claim"
                },
                {
                    path: "/vacancies/list",
                    title: "Vacancies",
                    type: "link",
                    permission: "view_vacancies"
                },
                {
                    path: "/field-employee-day/list",
                    title: "Field Employee Day",
                    type: "link",
                    icon: "users",
                    permission: "view_field_employee_day",
                    badge: true
                },
                {
                    path: "/bank-update-requests/list",
                    title: "Bank Update Requests",
                    icon: "dollar-sign",
                    type: "link",
                    permission: "view_bank_update_requests",
                    badge: true
                }
            ]
        },
        {
            title: "KYT Management",
            active: false,
            children: [
                {
                    title: "KYT",
                    icon: "box",
                    type: "sub",
                    active: false,
                    permission: "view_kyt",
                    children: [
                        {
                            path: "/kyts/marketing",
                            title: "Marketing KYT",
                            type: "link",
                            permission: "view_marketing_kyt"
                        },
                        {
                            path: "/kyts/operation",
                            title: "Operation KYT",
                            type: "link",
                            permission: "view_operation_kyt"
                        },
                        {
                            title: "KYT Transfer",
                            path: "/kyts/transfer/request",
                            type: "link",
                            active: false,
                            permission: "view_kyt_transfer"
                        },
                        {
                            title: "KYT Change",
                            path: "/kyts/change/request",
                            type: "link",
                            active: false,
                            permission: "view_kyt_change"
                        },
                        {
                            title: "KYT Log Chain",
                            type: "sub",
                            active: false,
                            permission: "view_kyt_chain_logs",
                            children: [
                               
                                {
                                    path: "/kyt-chain/logs",
                                    title: "All Logs",
                                    type: "link",
                                    permission: "view_kyt_chain_logs"
                                }
                            ]
                        }
                    ]
                },
                {
                    path: "/kyt-close/list",
                    title: "KYT Close - Operation",
                    type: "link",
                    permission: "view_kyt"
                },
                {
                    path: "/operation-change-transfer-kyt/kyt-transfer",
                    title: "KYT Operation Transfer",
                    type: "link",
                    permission: "view_operation_transfer_kyt"
                },
                {   
                    path: "/operation-change-transfer-kyt/kyt-change",
                    title: "KYT Operation Change",
                    type: "link",
                    permission: "view_operation_change_kyt"
                },
                {
                    path: "/kyt-close/list",
                    title: "KYT Close",
                    type: "link",
                    permission: "view_kyt_close"
                }
            ]
        },
        {
            title: "Front Office",
            active: false,
            children: [
                {
                    title: "Front Office",
                    icon: "monitor",
                    type: "sub",
                    active: false,
                    permission: "view_front_office",
                    children: [
                        {
                            path: "/front-office/pre-interview",
                            title: "Pre Interview",
                            type: "link",
                            permission: "view_pre_interview"
                        },
                        {
                            path: "/front-office/interview",
                            title: "Interview Panel 1",
                            type: "link",
                            permission: "view_interview",
                        },
                        {
                            path: "/front-office/interview/panel-2-list",
                            title: "Interview Panel 2",
                            type: "link",
                            permission: "view_interview_panel_2"
                        },
                        {
                            path: "/front-office/joining",
                            title: "Joining",
                            type: "link",
                            permission: "view_joining"
                        },
                        {
                            path: "/front-office/material-request",
                            title: "Front Office Material Request",
                            type: "link",
                            permission: "view_front_office_material_request"
                        },
                        {
                            path: "/front-office/order-delivery",
                            title: "Order Delivery",
                            type: "link",
                            permission: "view_order_delivery"
                        }
                    ]
                },
                {
                    path: "/pages/join-us-submissions",
                    title: "Join Us Forms",
                    icon: "box",
                    type: "link",
                    permission: "view_joinforms"
                },
                {
                    path: "/joining-finalization/list",
                    title: "Joining Finalization",
                    type: "link",
                    permission: "view_joining_finalization"
                }
            ]
        },
        {
            title: "Inventory Management",
            active: false,
            children: [
                {
                    title: "Uniform",
                    icon: "monitor",
                    type: "sub",
                    active: false,
                    permission: "view_uniform",
                    children: [
                        {
                            path: "/uniform/return",
                            title: "Return Uniform",
                            type: "link",
                            permission: "view_return_uniform"
                        },
                        {
                            path: "/uniform/issue",
                            title: "Issue Uniform",
                            type: "link",
                            permission: "view_issue_uniform"
                        }
                    ]
                },
                {
                    title: "Stocks",
                    icon: "box",
                    type: "sub",
                    active: false,
                    permission: "view_stocks",
                    children: [
                        {
                            path: "/stocks/out-of-stocks",
                            title: "Out of Stocks",
                            type: "link",
                            permission: "view_out_of_stocks"
                        }
                    ]
                },
                {
                    title: "Materials",
                    icon: "box",
                    type: "sub",
                    active: false,
                    permission: "view_materials",
                    children: [
                        {
                            path: "/materials/orders",
                            title: "Orders",
                            type: "link",
                            permission: "view_orders"
                        },
                        {
                            path: "/materials/goods",
                            title: "Goods",
                            type: "link",
                            permission: "view_goods"
                        }
                    ]
                },
                {
                    title: "Products",
                    path: "/products/list",
                    type: "link",
                    permission: "view_products"
                }
            ]
        },
        {
            title: "Document Management",
            active: false,
            children: [
                {
                    title: "Docs",
                    path: "/docs/list",
                    type: "link",
                    permission: "view_docs"
                },
                {
                    title: "Return Docs",
                    path: "/docs/return",
                    type: "link",
                    permission: "view_return_docs"
                },
                {
                    title: "Pending Docs",
                    path: "/pages/pending-docs",
                    type: "link",
                    permission: "view_pending_docs"
                }
            ]
        },
        {
            title: "Reports",
            active: false,
            children: [
                {
                    title: "Reports",
                    icon: "box",
                    type: "sub",
                    active: false,
                    permission: "view_reports",
                    children: [
                        {
                            title: "Operation Reports",
                            icon: "settings",
                            type: "sub",
                            active: false,
                            permission: "view_reports",
                            children: [
                                {
                                    path: "/reports/operation-reports/exchange",
                                    title: "Exchange Report",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/operation-reports/transfer",
                                    title: "Transfer Report",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/siddar-summary/report",
                                    title: "Siddar Summary",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/stock",
                                    title: "Stock Report",
                                    type: "link",
                                    permission: "view_reports"
                                }
                            ]
                        },
                        {
                            title: "FMMGC",
                            icon: "clipboard",
                            type: "sub",
                            active: false,
                            permission: "view_reports",
                            children: [
                                {
                                    path: "/reports/fmmgc/format",
                                    title: "Format",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/grooming",
                                    title: "Grooming",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/machine",
                                    title: "Machine",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/manpower",
                                    title: "Manpower",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/sop-knowledge",
                                    title: "SOP Knowledge",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/customised-sop",
                                    title: "Customised SOP",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/chemical-knowledge",
                                    title: "Chemical Knowledge",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/basic-hk-knowledge",
                                    title: "Basic HK Knowledge",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/fmmgc/machine-knowledge",
                                    title: "Machine Knowledge",
                                    type: "link",
                                    permission: "view_reports"
                                }
                            ]
                        },
                        {
                            title: "Office Reports",
                            icon: "monitor",
                            type: "sub",
                            active: false,
                            permission: "view_reports",
                            children: [
                                {
                                    title: "Training",
                                    type: "sub",
                                    active: false,
                                    permission: "view_reports",
                                    children: [
                                        {
                                            path: "/reports/training/security-training",
                                            title: "Security Training",
                                            type: "link",
                                            permission: "view_reports"
                                        },
                                        {
                                            path: "/reports/training/technical-training",
                                            title: "Technical Training",
                                            type: "link",
                                            permission: "view_reports"
                                        }
                                    ]
                                },
                                {
                                    path: "/reports/return-documents",
                                    title: "Return Documents",
                                    type: "link",
                                    permission: "view_reports"
                                },
                                {
                                    path: "/reports/training-briefing",
                                    title: "Training and briefing",
                                    type: "link",
                                    permission: "view_reports"
                                }
                            ]
                        },
                        {
                            path: "/reports/advance-salary-reports",
                            title: "Advance Salary Reports",
                            type: "link",
                            permission: "view_advance_salary_reports"
                        }
                    ]
                }
            ]
        },
        {
            title: "Project Management",
            active: false,
            children: [
                {
                    path: "/projects/list",
                    title: "Projects",
                    icon: "shopping-bag",
                    type: "link",
                    active: false,
                    permission: "view_projects"
                },
                {
                    path: "/site-creation/list",
                    title: "Site Creation",
                    type: "link",
                    permission: "view_site_creation"
                },
                {
                    path: "/new-sites/list",
                    title: "New Sites",
                    type: "link",
                    permission: "view_new_sites"
                }
            ]
        },
        {
            title: "Finance",
            active: false,
            children: [
                {
                    path: "/pv-details/list",
                    title: "PV Details",
                    type: "link",
                    permission: "view_pv_details"
                },
                {
                    path: "/pf-esic-details/list",
                    title: "PF/ESIC Details",
                    type: "link",
                    permission: "view_pf_esic_details"
                },
                {
                    path: "/variation-chart/list",
                    title: "Variation Chart",
                    type: "link",
                    permission: "view_variation_chart"
                },
                {
                    path: "/deduction-sheet/list",
                    title: "Deduction Sheet",
                    type: "link",
                    permission: "view_deduction_sheet"
                },
                {
                    path: "/chase-list/list",
                    title: "Chase List",
                    type: "link",
                    permission: "view_chase_list"
                },
                {
                    path: "/incentives/list",
                    title: "Incentives",
                    type: "link",
                    permission: "view_incentives"
                }
            ]
        },
        {
            title: "Settings",
            active: false,
            children: [
                {
                    title: "Settings",
                    icon: "settings",
                    type: "sub",
                    active: false,
                    permission: "view_settings",
                    children: [
                        {
                            path: "/users/list",
                            title: "Users",
                            icon: "users",
                            type: "link",
                            active: false,
                            permission: "view_users"
                        },
                        {
                            title: "Services",
                            path: "/settings/services",
                            type: "link",
                            permission: "view_services_settings"
                        },
                        {
                            title: "Designation",
                            path: "/settings/designation",
                            type: "link",
                            permission: "view_settings"
                        },
                        {
                            title: "Areas",
                            path: "/settings/areas",
                            type: "link",
                            permission: "view_area_management"
                        },
                        {
                            title: "Roles",
                            path: "/permissions/roles",
                            type: "link",
                            permission: "view_roles"
                        },
                        {
                            title: "Competitor",
                            path: "/settings/competitor",
                            type: "link",
                            permission: "view_competitor"
                        },
                        {
                            title: "Interview Questions",
                            path: "/interview-questions/list",
                            type: "link",
                            permission: "view_interview_questions"
                        },
                        {
                            title: "Department",
                            path: "/settings/department",
                            type: "link",
                            permission: "view_department"
                        },
                        {
                            title: "General Settings",
                            path: "/general-settings/list",
                            type: "link",
                            permission: "view_general_settings"
                        },
                        {
                            title: "Joining",
                            type: "sub",
                            active: false,
                            permission: "view_joining_settings",
                            children: [
                                {
                                    path: "/joining/assigning-panel",
                                    title: "Assigning Panel",
                                    type: "link",
                                    permission: "view_assigning_panel"
                                },
                                {
                                    path: "/joining/site-joining",
                                    title: "Site Joining",
                                    type: "link",
                                    permission: "view_site_joining"
                                }
                            ]
                        },
                        {
                            path: "/front-office/interview",
                            title: "Interview",
                            type: "link",
                            permission: "view_interview_settings"
                        },
                        {
                            path: "/category/list",
                            title: "Category list",
                            type: "link",
                            permission: "view_category_list"
                        },
                        {
                            path: "/category/subList",
                            title: "Sub category list",
                            type: "link",
                            permission: "view_subcategory_list"
                        },
                        {
                            path: "/subcategory-closure/pending-approvals",
                            title: "SubCategory Closure Admin",
                            type: "link",
                            permission: "view_subcategory_closure_admin"
                        },
                        {
                            path: "/leave-type/list",
                            title: "Siddar Type",
                            type: "link",
                            permission: "view_siddar_type"
                        },


                    ]
                }
            ]
        }
    ]
};

// Export the filtered menu
export const Menu: MenuData = {
    data: filterMenuSections(JSON.parse(JSON.stringify(rawMenu.data)))
};

// Debug log the final filtered menu
console.log('Final filtered menu:', Menu);

export default Menu;